/*eslint-disable*/
import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip, Chip } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { Visibility, Edit } from "@mui/icons-material";

// import for excel download
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import ApiDocsHeader from "components/ApiDocsComponents/Header/ApiDocsHeader";
import ApiAdminPopup from "components/ApiDocsComponents/Popup/ApiAdminPopup";
import ApiViewPopup from "../Popup/ApiViewPopup";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import ApiDocFilterPopup from "../Popup/ApiDocFilterPopup";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";

const ApiDocsListBody = () => {
  const token = localStorage.getItem("token");
  const { t } = useTranslation();

  // showSkeleton = true  → show skeleton (initial load + filter apply/reset)
  // showSkeleton = false → show table (search typing, pagination, etc.)
  const [showSkeleton, setShowSkeleton] = useState(false);

  const [apiList, setApiList] = useState([]);
  const [isDataReady, setIsDataReady] = useState(false);

  //optionally, you can manage the row selection state yourself
  const [rowSelection, setRowSelection] = useState({});
  const [selectedApiDocIds, setSelectedApiDocIds] = useState([]);

  // Track which doc is selected for view/edit
  const [selectedApiDocId, setSelectedApiDocId] = useState(null);
  const [viewApiDocId, setViewApiDocId] = useState(null);

  const [reloadData, setReloadData] = useState(false);
  const [isFilterReset, setIsFilterReset] = useState(false);

  // null = filter popup hasn't decided yet (skip API call)
  // []   = no default filter, fetch all
  // [...] = has filters, fetch filtered
  const [jsonFilterQuery, setJsonFilterQuery] = useState(null);

  // Global search states — MRT search is manual (server-side)
  const [globalSearch, setGlobalSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination -------------------------------------------
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10, //customize the default page size
  });

  const [lastPagination, setLastPagination] = useState({
    pageIndex: 0,
    pageSize: 10, //customize the default page size
  });

  // ─────────────────────────────────────────────
  // Fetch all API docs from backend
  // ─────────────────────────────────────────────
  const fetchApiDocs = async (
    filterQuery = [],
    searchTerm = "",
    withSkeleton = false,
  ) => {
    try {
      if (withSkeleton) setShowSkeleton(true);

      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_API_DOCS + `?token=${token}`;

      // Append filter conditions if present
      if (filterQuery.length > 0) {
        const sanitizedQuery = filterQuery.map((filter) => {
          if (
            (filter.operator === "includes" ||
              filter.operator === "excludes") &&
            Array.isArray(filter.value)
          ) {
            return {
              ...filter,
              value: filter.value.map((item) => item.value).join(","),
            };
          }
          return filter;
        });
        requestUrl += `&filters=${JSON.stringify(sanitizedQuery)}`;
      }

      // Append search term if present
      if (searchTerm.trim() !== "") {
        requestUrl += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      // console.log("request url------>", requestUrl);

      const response = await getData(requestUrl);

      if (response.status) {
        setApiList(response.data || []);
      } else {
        console.error("[ApiDocsListBody] Failed to fetch:", response.message);
        setApiList([]);
      }
    } catch (error) {
      console.error("[ApiDocsListBody] fetchApiDocs error:", error);
      setApiList([]);
    } finally {
      if (withSkeleton) setShowSkeleton(false);
    }
  };

  // ─────────────────────────────────────────────
  // Bulk delete API docs
  // ─────────────────────────────────────────────
  const bulkDeleteHandler = async () => {
    if (selectedApiDocIds.length > 0) {
      try {
        const apiData = {
          ids: selectedApiDocIds,
        };

        const requestUrl =
          url.API_BASE_URL + url.API_BULK_DELETE_API_DOC + `?token=${token}`;

        const response = await putData(requestUrl, apiData);

        if (response.status) {
          setTimeout(() => {
            setApiList((prevList) =>
              prevList.filter((doc) => !selectedApiDocIds.includes(doc._id)),
            );
            setRowSelection({}); // Clear row selection
            setSelectedApiDocIds([]); // Clear selection
          }, 1000);
        } else {
          console.error("Bulk delete failed:", response.message);
        }
      } catch (error) {
        console.log("Bulk delete error:", error.message);
      }
    } else {
    }
  };
  // ─────────────────────────────────────────────

  // ─────────────────────────────────────────────
  // Open offcanvas for editing an existing API doc
  // ─────────────────────────────────────────────
  const handleEdit = (id) => {
    setSelectedApiDocId(id);
    const offcanvasEl = document.getElementById("apiAdminPopup");
    if (offcanvasEl) {
      const bsOffcanvas = new window.bootstrap.Offcanvas(offcanvasEl);
      bsOffcanvas.show();
    }
  };

  // ─────────────────────────────────────────────
  // Open offcanvas for viewing API doc details (read-only)
  // ─────────────────────────────────────────────
  const handleView = (id) => {
    setViewApiDocId(id);
    const offcanvasEl = document.getElementById("apiViewPopup");
    if (offcanvasEl) {
      const bsOffcanvas = new window.bootstrap.Offcanvas(offcanvasEl);
      bsOffcanvas.show();
    }
  };

  // ─────────────────────────────────────────────
  // Table columns — mapped from schema fields
  // ─────────────────────────────────────────────
  const initialTableColumns = [
    {
      // Auto-incremented custom numeric ID
      accessorKey: "customid",
      header: t("API ID"),
      size: 90,
    },
    {
      // HTTP method: GET, POST, PUT, DELETE, PATCH
      accessorKey: "method",
      header: t("Method"),
      size: 100,
      Cell: ({ cell }) => {
        const method = cell.getValue();
        const colorMap = {
          GET: "success",
          POST: "primary",
          PUT: "warning",
          PATCH: "warning",
          DELETE: "error",
        };
        return (
          <Chip
            label={method}
            color={colorMap[method] || "default"}
            size="small"
            sx={{ fontWeight: 700, fontSize: "11px" }}
          />
        );
      },
    },
    {
      // Human-readable API name/title
      accessorKey: "title",
      header: t("API Name"),
      size: 200,
    },
    {
      // Module the API belongs to (e.g., Lead, User)
      accessorKey: "module",
      header: t("Module"),
      size: 130,
      Cell: ({ cell }) =>
        cell.getValue() || <span className="text-muted">—</span>,
    },
    {
      // Submodule within the module (e.g., Purchase, Auth)
      accessorKey: "submodule",
      header: t("Submodule"),
      size: 130,
      Cell: ({ cell }) =>
        cell.getValue() || <span className="text-muted">—</span>,
    },
    {
      // API endpoint path
      accessorKey: "endpoint",
      header: t("Endpoint"),
      size: 220,
      Cell: ({ cell }) => (
        <code style={{ fontSize: "12px", color: "#6c757d" }}>
          {cell.getValue()}
        </code>
      ),
    },
    {
      // API version (e.g., 1.0.0)
      accessorKey: "version",
      header: t("Version"),
      size: 100,
    },
    {
      // Whether the API requires authorization token
      accessorKey: "requiresauth",
      header: t("Auth Required"),
      size: 120,
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <Chip label={t("Yes")} color="warning" size="small" />
        ) : (
          <Chip label={t("No")} color="default" size="small" />
        ),
    },
    {
      // Active/Inactive status of the API
      accessorKey: "APIstatus",
      header: t("API Status"),
      size: 110,
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <Chip label={t("Active")} color="success" size="small" />
        ) : (
          <Chip label={t("Inactive")} color="error" size="small" />
        ),
    },
    {
      accessorKey: "ownerfullname",
      header: t("Owner"),
      size: 150,
    },
    {
      // Last updated timestamp
      accessorKey: "updatedAt",
      header: t("Last Updated"),
      size: 150,
      Cell: ({ cell }) => {
        const val = cell.getValue();
        if (!val) return <span className="text-muted">—</span>;
        return new Date(val).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
  ];

  // ─────────────────────────────────────────────
  // Material React Table Column and States
  // ─────────────────────────────────────────────
  const columns = useMemo(() => initialTableColumns, [t]);

  // ─────────────────────────────────────────────
  // Updates the column visibility state by toggling the visibility of the column with the given accessor key.
  // Represents the state of column visibility in the table.
  // ─────────────────────────────────────────────
  const initialVisibilityState = initialTableColumns.reduce((acc, column) => {
    acc[column.accessorKey] = true;
    return acc;
  }, {});

  // ─────────────────────────────────────────────
  // Updates the column visibility state based on the new column state.
  // ─────────────────────────────────────────────
  const [visibleColoumns, setVisibleColoumns] = useState(
    initialVisibilityState,
  );

  // ─────────────────────────────────────────────
  // function for column visibility
  // ─────────────────────────────────────────────
  const onColumnVisiblityHandler = (newColumnState) => {
    if (typeof newColumnState === "function") {
      const newColumnStateName = newColumnState();
      setVisibleColoumns((prev) => ({ ...prev, ...newColumnStateName }));
    } else {
      setVisibleColoumns(newColumnState);
    }
  };

  //initialize the column order
  const initialColumnOrder = [
    "mrt-row-actions",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ]; //array of column ids (Initializing is optional as of v2.10.0)

  const [columnOrder, setColumnOrder] = useState(initialColumnOrder);

  //function for change column order
  const changeColumnOrderHandler = (changedOrder) => {
    setColumnOrder(changedOrder);
  };

  //function for reset filter
  const resetFilterData = () => {
    setGlobalSearch("");
    setRowSelection({});
    setSelectedApiDocIds([]);
    setJsonFilterQuery(null);

    setReloadData(true);

    // reset pagination to default
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });

    setLastPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  };

  const refreshRecords = () => {
    resetFilterData();
    columnReset();
  };

  const columnReset = () => {
    setVisibleColoumns(initialVisibilityState);
    setColumnOrder(initialColumnOrder);
  };

  // ─────────────────────────────────────────────
  // Row action buttons — View and Edit
  // ─────────────────────────────────────────────
  const renderRowActions = ({ row }) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Tooltip title={t("View Details")}>
        <IconButton
          size="small"
          color="info"
          onClick={() => handleView(row.original._id)}
        >
          <Visibility sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("Edit API")}>
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleEdit(row.original._id)}
        >
          <Edit sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  useEffect(() => {
    if (jsonFilterQuery === null) return;

    if (jsonFilterQuery.length > 0) {
      fetchApiDocs(jsonFilterQuery, debouncedSearch, true);
    } else {
      fetchApiDocs([], debouncedSearch, true);
    }

    setIsDataReady(true);
  }, [jsonFilterQuery]);

  // Search effect — fires when debounced search changes.
  // Skeleton OFF — table stays visible while searching.
  useEffect(() => {
    if (!isDataReady) return;

    if (jsonFilterQuery && jsonFilterQuery.length > 0) {
      fetchApiDocs(jsonFilterQuery, debouncedSearch, false);
    } else {
      fetchApiDocs([], debouncedSearch, false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (reloadData) {
      fetchApiDocs([], "", true);
      setIsFilterReset(true);
      setReloadData(false);
    }
  }, [reloadData]);

  // save the last pagination
  useEffect(() => {
    setLastPagination({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    });
  }, [pagination.pageIndex, pagination.pageSize]);

  // ✅ Debounce — after typing 600ms, setDebouncedSearch will be called
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(globalSearch);
    }, 600);
    return () => clearTimeout(timer);
  }, [globalSearch]);

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key],
    );

    if (selectedIdsArray.length > 0) {
      setSelectedApiDocIds(selectedIdsArray);
    } else {
      setSelectedApiDocIds([]);
    }
  }, [rowSelection]);

  // ─────────────────────────────────────────────
  // download as excel
  // ─────────────────────────────────────────────
  const downloadAsExcel = () => {
    if (apiList.length > 0) {
      // Define headers matching MRT columns
      const headers = [
        t("API ID"),
        t("Method"),
        t("API Name"),
        t("Module"),
        t("Submodule"),
        t("Endpoint"),
        t("Version"),
        t("Auth Required"),
        t("API Status"),
        t("Owner"),
        t("Last Updated"),
      ];

      // Map apiList to rows matching MRT columns
      const data = apiList.map((item) => [
        item.customid || "",
        item.method || "",
        item.title || "",
        item.module || "",
        item.submodule || "",
        item.endpoint || "",
        item.version || "",
        item.requiresauth ? t("Yes") : t("No"),
        item.APIstatus ? t("Active") : t("Inactive"),
        item.ownerfullname || "",
        item.updatedAt
          ? new Date(item.updatedAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "",
      ]);

      // Create worksheet and workbook
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t("API Documentation"));

      // Write to buffer and save
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      const fileName = `${t("API Documentation")}_${new Date().toISOString().split("T")[0]}.xlsx`;
      FileSaver.saveAs(blob, fileName);
    }
  };

  // ==================== RENDER ====================
  return (
    <div id="content_wrapper">
      <section className="event-details-wrapper bg-white pb-5">
        <div className="container-fluid px-lg-5 pt-4 pt-md-0">
          <ApiDocsHeader
            breadCrumbText={[{ title: t("API Documentation") }]}
            reloadList={refreshRecords}
            downloadAsExcel={downloadAsExcel}
            jsonFilterQuery={jsonFilterQuery ?? []}
          />

          {!isDataReady || showSkeleton ? (
            <LoadingSkeleton />
          ) : (
            <div className="table-wrapper">
              <MaterialReactTable
                columns={columns}
                data={apiList}
                enableGrouping
                enableRowSelection // enable showing checkbox
                getRowId={(row) => row._id} // map which value to select with row checkbox
                onRowSelectionChange={setRowSelection} //connect internal row selection state to your own
                state={{
                  globalFilter: globalSearch || undefined,
                  rowSelection,
                  pagination: lastPagination,
                  columnVisibility: visibleColoumns,
                  columnOrder: columnOrder,
                }}
                onPaginationChange={setPagination} // set pagination
                autoResetPageIndex={false}
                initialState={{
                  rowSelection,
                  pagination: lastPagination,
                  columnVisibility: visibleColoumns,
                  columnOrder: columnOrder,
                }}
                enableColumnFilters={true}
                enableGlobalFilter={true}
                enableSorting={true}
                enablePagination={true}
                enableRowActions={true}
                enableColumnOrdering={true}
                positionActionsColumn="first"
                renderRowActions={renderRowActions}
                muiTableBodyRowProps={{
                  hover: true,
                }}
                muiTableContainerProps={{
                  sx: {
                    maxHeight: "60vh",
                  },
                }}
                enableStickyHeader
                onColumnVisibilityChange={onColumnVisiblityHandler}
                onColumnOrderChange={changeColumnOrderHandler}
                // ✅ MRT will not filter, API will filter

                // ✅ typing → setGlobalSearch → debounce → setDebouncedSearch → API call
                onGlobalFilterChange={(value) => setGlobalSearch(value ?? "")}
              />
            </div>
          )}
        </div>
      </section>

      {/* Add / Edit API Drawer */}
      <ApiAdminPopup
        selectedApiDocId={selectedApiDocId}
        setSelectedApiDocId={setSelectedApiDocId}
        onSaveSuccess={refreshRecords}
      />

      <ApiViewPopup
        selectedApiDocId={viewApiDocId}
        setSelectedApiDocId={setViewApiDocId}
      />

      <DeleteWarningModal
        moduleName="ApiDocs"
        onDelete={bulkDeleteHandler}
        onCancelDelete={() => {
          setRowSelection({});
          setSelectedApiDocIds([]);
        }}
      />

      <ApiDocFilterPopup
        moduleSlug="apidocs"
        setJsonFilterQuery={setJsonFilterQuery}
        reloadList={refreshRecords}
        isFilterReset={isFilterReset}
        setIsFilterReset={setIsFilterReset}
        visibleColoumns={visibleColoumns}
        setVisibleColoumns={setVisibleColoumns}
        columnOrder={columnOrder}
        setColumnOrder={setColumnOrder}
      />
    </div>
  );
};

export default ApiDocsListBody;
